/* eslint-disable no-console */
/* eslint-disable react-hooks/exhaustive-deps */
import React, { useCallback, useContext, useState } from 'react';
import { useLazyQuery } from '@apollo/client';
import { Button, Dropdown, DropdownItem, DropdownMenu, DropdownToggle, Spinner } from 'reactstrap';
import { RiDownload2Line } from 'react-icons/ri';
import { authContext } from 'contexts/AuthContext';
import * as XLSX from 'xlsx';

const DownloadCSV = ({
  lazyQuery,
  filters,
  apiKey,
  classWrapper = '',
  positionTop = '-30px',
  positionRight = '0',
  fileName,
  url,
  notUseGraphQL = false,
  formats = ['csv'],
}) => {
  const [fetchData, { loading, error }] = useLazyQuery(lazyQuery);
  const [apiCallLoading, setApiCallLoading] = useState(false);
  const [apiCallError, setApiCallError] = useState(null);
  const [isFormatMenuOpen, setIsFormatMenuOpen] = useState(false);
  const { auth } = useContext(authContext);
  const supportsMultipleFormats = formats.length > 1;

  const getExportText = useCallback((data) => {
    const base64String = data?.[apiKey];
    if (!base64String) {
      throw new Error('The export response did not contain CSV data.');
    }

    const bytes = Uint8Array.from(atob(base64String), (char) => char.charCodeAt(0));
    return new TextDecoder('utf-8').decode(bytes);
  }, [apiKey]);

  const downloadBlob = useCallback((blob, extension) => {
    const objectUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = objectUrl;
    link.download = `${fileName}.${extension}`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(objectUrl);
  }, [fileName]);

  const handleExportDownload = useCallback((data, format) => {
    const csvText = getExportText(data);
    if (format === 'excel') {
      const workbook = XLSX.read(csvText, { type: 'string' });
      const excelData = XLSX.write(workbook, {
        bookType: 'xlsx',
        type: 'array',
      });
      downloadBlob(
        new Blob([excelData], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        }),
        'xlsx',
      );
      return;
    }
    downloadBlob(new Blob(['\uFEFF', csvText], { type: 'text/csv;charset=utf-8' }), 'csv');
  }, [downloadBlob, getExportText]);

  const handleCsvDownloadWithoutBase64 = async (response) => {
    // Get the blob from response
    const blob = await response.blob();

    // Create download link
    const urlRedirection = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = urlRedirection;

    link.download = `${fileName}.csv`;
    document.body.appendChild(link);
    link.click();

    // Cleanup
    document.body.removeChild(link);
    window.URL.revokeObjectURL(urlRedirection);
  };

  const handleClick = async (format) => {
    try {
      setIsFormatMenuOpen(false);
      if (notUseGraphQL) {
        setApiCallLoading(true);
        setApiCallError(null);

        const response = await fetch(url, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${auth?.data?.token}`,
          },
        });

        if (!response.ok) {
          const responseBody = await response.json();
          setApiCallError(responseBody?.message || 'Failed to download CSV');
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        if (format === 'excel') {
          const csvText = await response.text();
          const workbook = XLSX.read(csvText, { type: 'string' });
          const excelData = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
          downloadBlob(new Blob([excelData], {
            type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
          }), 'xlsx');
        } else {
          await handleCsvDownloadWithoutBase64(response);
        }
        setApiCallError(null);
      } else {
        setApiCallLoading(true);
        setApiCallError(null);
        const { data } = await fetchData({
          variables: {
            ...filters,
          },
        });
        if (data) {
          handleExportDownload(data, format);
        } else {
          console.info('Data object not found when downloading CSV.');
        }
      }
    } catch (err) {
      console.error(err);
      setApiCallError(err?.message || 'Failed to download CSV');
    } finally {
      setApiCallLoading(false);
    }
  };

  if (error || apiCallError)
    return (
      <div className="f-12 text-danger">
        {error
          ? error?.graphQLErrors.map(({ exception }) => {
              return exception?.message;
            })
          : apiCallError}
      </div>
    );

  return (
    supportsMultipleFormats ? (
      <Dropdown
        isOpen={isFormatMenuOpen}
        toggle={() => setIsFormatMenuOpen((open) => !open)}
        className={`download-csv-dropdown d-inline-block ${classWrapper}`}
        style={{ marginTop: positionTop, marginRight: positionRight }}
      >
        <DropdownToggle
          tag={Button}
          className={`gray-btn csv-btn ${classWrapper}`}
          color="link"
          size="sm"
          type="button"
          disabled={loading || apiCallLoading}
        >
          {loading || apiCallLoading ? (
            <Spinner color="primary" size="sm" className="small-loader" style={{ marginTop: '1px' }} />
          ) : (
            <RiDownload2Line size={16} className="fw-bold" />
          )}
        </DropdownToggle>
        <DropdownMenu end>
          {formats.includes('csv') && (
            <DropdownItem onClick={() => handleClick('csv')} disabled={loading || apiCallLoading}>
              Download CSV
            </DropdownItem>
          )}
          {formats.includes('excel') && (
            <DropdownItem onClick={() => handleClick('excel')} disabled={loading || apiCallLoading}>
              Download Excel (.xlsx)
            </DropdownItem>
          )}
        </DropdownMenu>
      </Dropdown>
    ) : (
      <span className="download-csv-single">
        <Button
          className={`gray-btn csv-btn ${classWrapper}`}
          color="link"
          size="sm"
          type="button"
          disabled={loading || apiCallLoading}
          style={{ marginTop: positionTop, marginRight: positionRight }}
          onClick={() => handleClick(formats[0] || 'csv')}
        >
          {loading || apiCallLoading ? (
            <Spinner
              color="primary"
              size="sm"
              className="small-loader"
              style={{ marginTop: '1px' }}
            />
          ) : (
            <RiDownload2Line size={16} className="fw-bold" />
          )}
        </Button>
      </span>
    )
  );
};

export default DownloadCSV;
